import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import leadCrmImg from './assets/feature-lead-crm.svg';
import websiteBuilderImg from './assets/feature-website-builder.svg';
import automationImg from './assets/feature-automation.svg';
import universalInboxImg from './assets/feature-universal-inbox.svg';

const tabs = ['Lead CRM', 'Website Builder', 'Automation', 'Universal Inbox'];

const cards = [
    {
        label: 'We help you',
        heading: 'organize leads effortlessly',
        description:
            "We'll centralize your entire database\u2014from cold prospects, open house visitors, to active buyers\u2014to help you track every interaction to most effectively close your deals.",
        footer:
            'Kayros has tools for bulk import and export. We aim to keep your data clean and accessible.',
        image: leadCrmImg,
        imageAlt: 'Lead CRM product preview',
    },
    {
        label: 'We help you',
        heading: 'publish sites instantly',
        description:
            "If you're like many of our agents who are currently marketing listings, you likely have many questions regarding tech:",
        bullets: [
            'Do I need coding skills to launch?',
            'Which designs convert the best visitors?',
            'How can I capture leads from the page?',
        ],
        footer:
            'Kayros has optimized templates available. We aim to drive traffic to your personal brand.',
        image: websiteBuilderImg,
        imageAlt: 'Website Builder preview',
    },
    {
        label: 'We help you',
        heading: 'follow up automatically',
        description:
            "We'll trigger a sequence of actions across SMS replies, email drip campaigns, and task reminders to help you nurture leads and most effectively save your time.",
        footer:
            'Kayros has "Stop on Reply" technology built-in. We aim to make automation feel completely human.',
        image: automationImg,
        imageAlt: 'Automation workflow preview',
    },
    {
        label: 'We help you',
        heading: 'communicate without chaos',
        description:
            "We'll sync a variety of messaging platforms across Twilio SMS, official email, and website chat to help you respond to clients to most effectively build trust.",
        footer:
            'Kayros has unified interaction history logs. We aim to provide context for every single conversation.',
        image: universalInboxImg,
        imageAlt: 'Unified inbox preview',
    },
];

const DEFAULT_TAB = 2; // "Automation" active by default

function FeatureCard({ card }) {
    return (
        <div className="w-full h-full rounded-3xl border border-gray-200 bg-white shadow-[0_4px_32px_rgba(0,0,0,0.06)] p-10 md:p-14 lg:p-16 flex flex-col md:flex-row items-stretch gap-8 md:gap-12 select-none">
            {/* Left — text content */}
            <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                    <p className="text-2xl md:text-[28px] font-light text-gray-400 leading-snug">
                        {card.label}
                    </p>
                    <h2 className="text-2xl md:text-[28px] font-medium text-gray-900 mb-6 md:mb-8 leading-snug">
                        {card.heading}
                    </h2>
                    <p className="text-gray-600 text-[15px] md:text-base leading-relaxed">
                        {card.description}
                    </p>
                    {card.bullets && (
                        <ul className="list-disc list-inside text-gray-600 text-[15px] md:text-base leading-relaxed mt-3 space-y-1">
                            {card.bullets.map((b) => (
                                <li key={b}>{b}</li>
                            ))}
                        </ul>
                    )}
                </div>
                <p className="text-sm text-gray-400 mt-12 md:mt-16 leading-relaxed">
                    {card.footer}
                </p>
            </div>

            {/* Right — feature preview */}
            <div className="w-full md:w-[320px] lg:w-[360px] flex-shrink-0 flex items-center justify-center">
                <div className="w-full rounded-2xl border border-gray-100 bg-gray-50 shadow-sm overflow-hidden">
                    <img
                        src={card.image}
                        alt={card.imageAlt || 'Product UI preview'}
                        className="w-full h-auto object-contain"
                    />
                </div>
            </div>
        </div>
    );
}

export default function AdviceSection() {
    const [currentIndex, setCurrentIndex] = useState(DEFAULT_TAB + 1);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const trackRef = useRef(null);
    const touchStartX = useRef(0);

    const slideCount = cards.length;
    const loopedCards = [cards[cards.length - 1], ...cards, cards[0]];

    // Derive the real 0-based tab index from the looped index
    const realIndex =
        currentIndex <= 0
            ? slideCount - 1
            : currentIndex >= slideCount + 1
                ? 0
                : currentIndex - 1;

    const prevSlide = useCallback(() => {
        setIsTransitioning(true);
        setCurrentIndex((prev) => prev - 1);
    }, []);

    const nextSlide = useCallback(() => {
        setIsTransitioning(true);
        setCurrentIndex((prev) => prev + 1);
    }, []);

    // Infinite-loop boundary reset
    useEffect(() => {
        const handleTransitionEnd = () => {
            if (currentIndex <= 0) {
                setIsTransitioning(false);
                setCurrentIndex(slideCount);
            }
            if (currentIndex >= slideCount + 1) {
                setIsTransitioning(false);
                setCurrentIndex(1);
            }
        };
        const track = trackRef.current;
        if (track) track.addEventListener('transitionend', handleTransitionEnd);
        return () => {
            if (track) track.removeEventListener('transitionend', handleTransitionEnd);
        };
    }, [currentIndex, slideCount]);

    // Re-enable smooth transition after instant jump
    useEffect(() => {
        if (!isTransitioning) {
            requestAnimationFrame(() =>
                requestAnimationFrame(() => setIsTransitioning(true)),
            );
        }
    }, [isTransitioning]);

    const handleTabClick = (index) => {
        setIsTransitioning(true);
        setCurrentIndex(index + 1);
    };

    // Touch / swipe support for mobile
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            diff > 0 ? nextSlide() : prevSlide();
        }
    };

    // Carousel geometry — left-aligned slider.
    // The active card sits flush with the container's left padding edge.
    // The next card peeks ~18 % into view on the right.
    const CARD_WIDTH = 82; // % of the max-w-7xl container
    const GAP_REM = 1.5;   // rem between cards

    // Each step shifts by one card-width + one gap
    const translatePct = -(currentIndex * CARD_WIDTH);
    const translateGapRem = currentIndex * GAP_REM;

    return (
        <section className="w-full bg-white py-16 md:py-24 relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Heading */}
                <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 leading-tight">
                    Everything you need <br className="hidden md:inline" />
                    <span className="text-gray-400">to scale</span>
                </h2>

                {/* Tabs + Arrows */}
                <div className="flex max-md:flex-col items-center justify-between mt-8 md:mt-10 max-md:gap-4">
                    <div className="flex flex-wrap gap-7 max-md:gap-4 max-md:justify-center">
                        {tabs.map((tab, index) => (
                            <button
                                key={tab}
                                onClick={() => handleTabClick(index)}
                                className={`pb-2 text-sm transition-colors ${realIndex === index
                                        ? 'border-b-2 border-black text-black font-medium'
                                        : 'text-gray-400 hover:text-gray-700'
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={prevSlide}
                            className="bg-black text-white p-2.5 max-md:p-2 rounded-full shadow-md hover:bg-gray-700 transition-colors"
                            aria-label="Previous feature"
                        >
                            <ChevronLeft size={24} />
                        </button>
                        <button
                            onClick={nextSlide}
                            className="bg-black text-white p-2.5 max-md:p-2 rounded-full shadow-md hover:bg-gray-700 transition-colors"
                            aria-label="Next feature"
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </div>

                {/* Carousel — left-aligned; next card peeks on the right */}
                <div
                    className="mt-10 md:mt-12 relative"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div
                        ref={trackRef}
                        className={`flex items-stretch ${isTransitioning
                                ? 'transition-transform duration-700 ease-in-out'
                                : ''
                            }`}
                        style={{
                            transform: `translateX(calc(${translatePct}% - ${translateGapRem}rem))`,
                            gap: `${GAP_REM}rem`,
                        }}
                    >
                        {loopedCards.map((card, index) => {
                            const isActive = index === currentIndex;
                            return (
                                <div
                                    key={index}
                                    className="flex-shrink-0"
                                    style={{
                                        width: `${CARD_WIDTH}%`,
                                        opacity: isActive ? 1 : 0.35,
                                        transform: isActive ? 'scale(1)' : 'scale(0.97)',
                                        transition:
                                            'opacity 0.7s ease-in-out, transform 0.7s ease-in-out',
                                    }}
                                >
                                    <FeatureCard card={card} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
